import type { AccountStatisticsResponse, StatisticsResponse } from '@1webit/cvs-api-scheme';
import type { GetApiV1BankAccountsTypeTypeParams } from '@1webit/cvs-api-scheme/models';
import { queryOptions } from '@tanstack/react-query';

import type { TransactionType } from '@/app/(dashboard)/bank/list/[id]/_components/my-account-history-table';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/constants';
import { dateToAPIFormat, getResponse, queryParams } from '@/utils';

export enum BankAccountType {
  CVS = 'cvs',
  Regional = 'regional',
  District = 'district',
  Other = 'other',
}

interface MappedAccountStatisticsResponse extends AccountStatisticsResponse {
  id: number;
}

interface MappedStatisticsResponse {
  cvs: {
    income: number;
    expense: number;
    balance: number;
    accounts: MappedAccountStatisticsResponse[];
  };
  regional: {
    income: number;
    expense: number;
    balance: number;
    accounts: MappedAccountStatisticsResponse[];
  };
  district: {
    income: number;
    expense: number;
    balance: number;
    accounts: MappedAccountStatisticsResponse[];
  };
  other: {
    stoneBankIncome: number;
    stoneBankExpense: number;
    stoneBankBalance: number;
    cvsIncome: number;
    cvsExpense: number;
    cvsBalance: number;
  };
}
export const getApiV1BankAccounts = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1BankAccounts', page, limit, sort],
    queryFn: () => getResponse('getApiV1BankAccounts', queryParams({
      limit,
      offset: (page - 1) * limit,
      sort,
    })),
  });
};
export const getApiV1BankPayment = (ks?: string, vs?: string, text?: string, dateFrom?: string, dateTo?: string, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1BankPayment', ks, vs, text, dateFrom, dateTo, page, limit, sort],

    queryFn: async () => {
      const response = await getResponse('getApiV1BankPayment', queryParams({
        limit,
        offset: (page - 1) * limit,
        sort,
        dateFrom,
        dateTo,
        ks,
        vs,
        text,
      }));
      const { meta, responseData } = response.payments;

      return {
        ...response,
        meta,
        responseData: responseData.map(item => ({
          ...item,
          id: item.id,
        })),
      };
    },
  });
};
export const getApiV1BankAccountsAccountIdTransactions = (id: number, ks?: number, type?: TransactionType, dateFrom?: string, dateTo?: string, fullText?: string, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1BankAccountsAccountIdTransactions', id, ks, type, dateFrom, dateTo, fullText, page, limit, sort],
    queryFn: () => getResponse('getApiV1BankAccountsAccountIdTransactions', id, queryParams({
      dateFrom,
      dateTo,
      ks,
      type,
      fullText,
      limit,
      offset: (page - 1) * limit,
      sort,
    })),
  });
};

export const getApiV1BankPaymentPaymentId = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1BankPaymentPaymentId', id],
    queryFn: () => getResponse('getApiV1BankPaymentPaymentId', id),
  });
};
export const getApiV1BankAccountsAccountId = (bankId: number) => {
  return queryOptions({
    queryKey: ['getApiV1BankAccountsAccountId', bankId],
    queryFn: () => getResponse('getApiV1BankAccountsAccountId', bankId),
  });
};

export const getApiV1TransactionTransactionIdDetail = (detailId: number, accountId: number) => {
  return queryOptions({
    queryKey: ['getApiV1TransactionTransactionIdDetail', detailId, accountId],
    queryFn: () => getResponse('getApiV1TransactionTransactionIdDetail', detailId, { accountId }),
  });
};

const defaultDate = dateToAPIFormat(new Date());
const defaultDayBefore = dateToAPIFormat(new Date().setDate(new Date().getDate() - 1));

export const getApiV1BankAccountsTypeQueryOptions = (
  accountType: BankAccountType,
  page = 1,
  limit = DEFAULT_TABLE_PAGE_SIZE,
  params?: Omit<GetApiV1BankAccountsTypeTypeParams, 'limit' | 'offset'>,
) => {
  return queryOptions({
    queryKey: ['getApiV1BankAccountsType', accountType, page, limit, params],
    queryFn: () =>
      getResponse(
        'getApiV1BankAccountsTypeType',
        accountType,
        queryParams({
          ...params,
          limit,
          offset: (page - 1) * limit,
        }),
      ),
  });
};
export const getApiV1BankAccountsStatisticsQueryOptions = (
  dateFrom: string = defaultDayBefore,
  dateTo: string = defaultDate,
  page = 1,
  limit = DEFAULT_TABLE_PAGE_SIZE,
  sort?: string,
) => {
  return queryOptions({
    queryKey: ['getApiV1BankAccounts', dateFrom, dateTo, page, limit, sort],
    queryFn: async (): Promise<MappedStatisticsResponse> => {
      const response: StatisticsResponse = await getResponse(
        'getApiV1BankAccountsStatistics',
        queryParams({
          dateFrom,
          dateTo,
        }),
      );

      const mappedResponse: MappedStatisticsResponse = {
        cvs: {
          ...response.cvs,
          accounts: response.cvs.accounts.map(account => ({
            ...account,
            id: Number(account.accountId),
          })),
        },
        regional: {
          ...response.regional,
          accounts: response.regional.accounts.map(account => ({
            ...account,
            id: Number(account.accountId),
          })),
        },
        district: {
          ...response.district,
          accounts: response.district.accounts.map(account => ({
            ...account,
            id: Number(account.accountId),
          })),
        },
        other: {
          ...response.other,
        },
      };

      return mappedResponse;
    },
  });
};

export const getApiV1BankUserRights = (accountId: number, transactionId?: number) => queryOptions({
  queryKey: ['getApiV1BankUserRights', accountId, transactionId ?? null],
  queryFn: () => getResponse('getApiV1BankUserRights', {
    accountId,
    ...(transactionId !== undefined ? { transactionId } : {}),
  }),
});

export const getApiV1BankRequestPaymentRequestId = (requestId: number) => queryOptions({
  queryKey: ['getApiV1BankRequestPaymentRequestId', requestId],
  queryFn: () => getResponse('getApiV1BankRequestPaymentRequestId', requestId),
});

export const getApiV1BankIdAccountName = (accountId: number) =>
  queryOptions({
    queryKey: ['getApiV1BankIdAccountName', accountId],
    queryFn: () => getResponse('getApiV1BankIdAccountName', accountId),
  });
